import { defineStore } from "pinia";
import axios from "@/app/plugins/axios";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";
import {
  CatalogState,
  CatalogProductClass,
  CatalogProductClassItem,
  CatalogProductItem,
  CatalogItem,
  CatalogPrice,
} from "@/catalog/types/catalog";
import { getErrorMessage } from "@/shared/utils/errorMessage";

export const useCatalogStore = defineStore("catalog", {
  state: (): CatalogState => ({
    catalog: {},
    loading: false,
    error: undefined,
  }),

  getters: {
    isLoading: (state) => state.loading,
    getCatalog: (state) => state.catalog,

    // Get flattened array of product classes with their products
    getProductsWithPrices: (state): CatalogItem[] => {
      const result: CatalogItem[] = [];

      // Iterate through product classes
      Object.entries(state.catalog).forEach(([productClassId, productClass]) => {
        // Add product class level entry
        const productClassItem: CatalogProductClassItem = {
          id: productClassId,
          name: productClass.name,
          active: productClass.active,
          type: "product_class",
          description: productClass.description || "",
        };
        result.push(productClassItem);

        // Add product level entries if products exist
        if (productClass.products) {
          Object.entries(productClass.products).forEach(([productId, product]) => {
            // Get default price information if available
            let defaultPriceInfo: CatalogPrice | undefined = undefined;
            if (product.default_price && product.prices && product.prices[product.default_price]) {
              defaultPriceInfo = product.prices[product.default_price];
            }

            const productItem: CatalogProductItem = {
              id: productId,
              name: product.name,
              type: "product",
              description: product.description || "",
              productClassId: productClassId,
              productClassName: productClass.name,
              sku: product.sku,
              defaultPriceId: product.default_price,
              defaultPriceInfo: defaultPriceInfo,
              allPrices: product.prices,
            };
            result.push(productItem);

            // Optionally add price level entries if needed
            // Currently omitting as per requirements to only show default price
            /*
            Object.entries(product.prices).forEach(([priceId, price]) => {
              const priceItem: CatalogPriceItem = {
                id: priceId,
                productId: productId,
                productName: product.name,
                type: 'price',
                nickname: price.nickname,
                unit_amount: price.unit_amount,
                active: price.active
              };
              result.push(priceItem);
            });
            */
          });
        }
      });

      return result;
    },
  },

  actions: {
    async fetchCatalog() {
      try {
        this.loading = true;
        this.error = undefined;

        const response = await axios.get<Record<string, CatalogProductClass>>("/bundle/catalog");
        this.catalog = response.data || {};

        return this.catalog;
      } catch (error) {
        this.error = getErrorMessage(error, i18n.global.t("common.action.fetchFailed"));
        useSnackBarStore().toastError({
          message: this.error,
        });
        return {};
      } finally {
        this.loading = false;
      }
    },
  },
});
